import React, { useContext, useLayoutEffect, useRef } from 'react';
import { UNSAFE_LocationContext, useLocation, useNavigate, useOutlet } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { rememberListPaths, shouldRestoreListSearch } from './listViewCache';

type LocationContextValue = React.ContextType<typeof UNSAFE_LocationContext>;

type ScrollSnap = {
  windowY: number;
  tableTops: number[];
};

/**
 * Keeps recently visited screens mounted under the dashboard outlet.
 * Opening a record and coming back (in-app Back or the browser Back button)
 * restores filters, search, sort, pagination, column choices, and scroll
 * because the list component is not remounted. Clear / Reset still works:
 * that is the page's own state update.
 */
export const KeepAliveOutlet: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const locationContext = useContext(UNSAFE_LocationContext);
  const companyId = useAuthStore((s) => s.user?.company_id ?? null);

  const outlets = useRef(new Map<string, React.ReactElement | null>());
  const frozenLocation = useRef(new Map<string, LocationContextValue>());
  const searchByPath = useRef(new Map<string, string>());
  const scrollByPath = useRef(new Map<string, ScrollSnap>());
  const paneRefs = useRef(new Map<string, HTMLDivElement | null>());
  const order = useRef<string[]>([]);
  const prevPath = useRef(location.pathname);
  const arrivedFrom = useRef<string | null>(null);
  const companyRef = useRef(companyId);

  if (companyRef.current !== companyId) {
    companyRef.current = companyId;
    outlets.current.clear();
    frozenLocation.current.clear();
    searchByPath.current.clear();
    scrollByPath.current.clear();
    order.current = [];
    prevPath.current = location.pathname;
    arrivedFrom.current = null;
  }

  if (prevPath.current !== location.pathname) {
    const leaving = prevPath.current;
    arrivedFrom.current = leaving;
    scrollByPath.current.set(leaving, readScroll(paneRefs.current.get(leaving) ?? null));
    prevPath.current = location.pathname;
  } else {
    searchByPath.current.set(location.pathname, location.search);
  }

  // Never cache a null outlet — that poisoned Approval Inbox → document deep links
  // (e.g. brief legacy /sales redirect frames) and left a blank active pane until refresh.
  if (outlet != null) {
    outlets.current.set(location.pathname, outlet);
  } else {
    outlets.current.delete(location.pathname);
  }
  if (locationContext) frozenLocation.current.set(location.pathname, locationContext);

  const paths = rememberListPaths(order.current, location.pathname);
  order.current = paths;
  for (const key of outlets.current.keys()) {
    if (!paths.includes(key)) {
      outlets.current.delete(key);
      frozenLocation.current.delete(key);
      searchByPath.current.delete(key);
      scrollByPath.current.delete(key);
      paneRefs.current.delete(key);
    }
  }

  useLayoutEffect(() => {
    const from = arrivedFrom.current;
    const rememberedSearch = searchByPath.current.get(location.pathname);
    if (
      from &&
      shouldRestoreListSearch(from, location.pathname) &&
      rememberedSearch != null &&
      rememberedSearch !== location.search
    ) {
      const next: Partial<Location> = {
        pathname: location.pathname,
        search: rememberedSearch,
        hash: location.hash,
      };
      navigate(next, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  useLayoutEffect(() => {
    const snap = scrollByPath.current.get(location.pathname);
    const pane = paneRefs.current.get(location.pathname) ?? null;
    if (snap) restoreScroll(pane, snap);
  }, [location.pathname]);

  return (
    <>
      {paths.map((path) => {
        const active = path === location.pathname;
        const frozen = frozenLocation.current.get(path) ?? locationContext;
        // Prefer cached node; for the active path always fall back to the live outlet
        // so a missed/cleared cache cannot render a blank screen.
        const node = outlets.current.get(path) ?? (active ? outlet : null);
        if (!node || !frozen) return null;
        return (
          <div
            key={path}
            ref={(el) => {
              paneRefs.current.set(path, el);
            }}
            hidden={!active}
            style={active ? undefined : { display: 'none' }}
          >
            <UNSAFE_LocationContext.Provider value={active ? locationContext ?? frozen : frozen}>
              {node}
            </UNSAFE_LocationContext.Provider>
          </div>
        );
      })}
    </>
  );
};

function readScroll(root: HTMLElement | null): ScrollSnap {
  const tableTops = root
    ? Array.from(root.querySelectorAll<HTMLElement>('.ant-table-body')).map((el) => el.scrollTop)
    : [];
  return { windowY: window.scrollY, tableTops };
}

function restoreScroll(root: HTMLElement | null, snap: ScrollSnap): void {
  window.scrollTo(0, snap.windowY);
  if (!root) return;
  root.querySelectorAll<HTMLElement>('.ant-table-body').forEach((el, index) => {
    const top = snap.tableTops[index];
    if (top != null) el.scrollTop = top;
  });
}

export default KeepAliveOutlet;
